import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../../../../../src/components/common/skeleton';
import {CrmListModel} from '../../../../../../../../../src/models/api/crmListModel';
import {CrmSyncSearchListsModel} from '../../../../../../../../../src/models/api/crmSearchListsModel';
import {CrmSyncSearchOptions} from '../../../../../../../../../src/store/data/crmSync/crmSyncActions';
import {openInBrowser} from '../../../../../../../../../srcCommon/browser/openInBrowser';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {RadioButton} from '../../../../../../../../shared/ui/src/components/booleanInputs/radioButton';
import {DropdownBox} from '../../../../../../../../shared/ui/src/components/boxedInputs/dropdownBox';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {CrmSyncsPageResourceConfig, CrmSyncsResourceOptions} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsSectionWrapper} from '../crmSyncsSectionWrapper';
import {CrmSyncsListsDropdown} from './crmSyncsListsDropdown';

/*
 * Constants.
 */

const maxTooltipWidth = 240; // px.

/*
 * Types.
 */

export interface CrmSyncsOptionsProps {
  isDisabled: boolean;
  isAdmin: boolean;
  isLoading?: boolean;
  isLoadingList?: boolean;
  resourceConfig: CrmSyncsPageResourceConfig;
  crmDisplayName: string;
  allResourceCount?: number;
  selectedOption?: SelectedOption;
  disabledOptionType?: CrmSyncOptionsEnum;
  list?: CrmListModel;
  listError?: ListErrorsEnum;
  onSelect: (type: CrmSyncOptionsEnum, listId?: string) => void;
  searchListsAsync: (
    query: string,
    options: CrmSyncSearchOptions,
  ) => Promise<CrmSyncSearchListsModel | undefined>;
}

// matches https://github.com/frontapp/front/blob/master/server/resources/sync/sync_types.ts#L139
export const EXTERNAL_LINK_SYNC_ALL = 'all';

export enum CrmSyncOptionsEnum {
  ALL = 'ALL',
  ACCOUNTS = 'ACCOUNTS',
  LISTS = 'LISTS',
}

export interface SelectedOption {
  type: CrmSyncOptionsEnum;
  listId?: string;
  count?: number;
}

export enum ListErrorsEnum {
  LIST_DELETED = 'list_deleted',
  MAX_LIMIT_REACHED = 'max_limit_reached',
  UNKNOWN = 'unknown',
}

/*
 * Style.
 */

const StyledMaxCountErrorWrapperDiv = styled.div`
  margin: 8px 0 0 26px;
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: 12px;
`;

const StyledListErrorWrapperDiv = styled.div`
  margin-top: 4px;
`;

const StyledSkeleton = styled(Skeleton)`
  display: inline-block;
  margin: 8px 0 -2px 24px;
  width: 150px;
  height: 30px;
`;

const StyledRadioButton = styled(RadioButton)`
  margin-top: 12px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
  margin: 0 0 -3px 4px;
`;

const StyledTooltipLearnMoreSpan = styled.span`
  font-weight: ${fontWeights.bold};
  text-decoration: underline;
`;

const StyledDropdownBox = styled(DropdownBox)`
  display: inline-block;
  margin: 8px 0 0 24px;
`;

const StyledListsDropdownWrapper = styled.div`
  width: fit-content;
`;

const StyledCountSpan = styled.span`
  margin-left: 4px;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const CrmSyncsOptions: FC<CrmSyncsOptionsProps> = (props) => {
  const {isDisabled, isLoading, isAdmin, resourceConfig, onSelect} = props;
  const {syncOptionsTitle, syncOptions} = resourceConfig;

  return (
    <CrmSyncsSectionWrapper
      title={syncOptionsTitle}
      isDisabled={isDisabled}
      isLoading={isLoading}
      renderLoadingState={renderLoadingState}
    >
      {renderOptions(props, syncOptions, Boolean(isDisabled || !isAdmin), onSelect)}
    </CrmSyncsSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderOptions(
  props: CrmSyncsOptionsProps,
  syncOptions: CrmSyncsResourceOptions,
  isSectionDisabled: boolean,
  onSelect: (type: CrmSyncOptionsEnum, listId?: string) => void,
) {
  const {selectedOption, disabledOptionType, allResourceCount, resourceConfig} = props;
  return Object.entries(syncOptions).map(([type, value]) => {
    const isChecked = selectedOption?.type === type;
    const isDisabled = disabledOptionType === type || isSectionDisabled;
    const maxCountError = extractMaxCountError(resourceConfig, CrmSyncOptionsEnum.ALL);

    if (type === CrmSyncOptionsEnum.LISTS) {
      return maybeRenderListsOption(props, syncOptions, resourceConfig.maxNumberToSync, (listId?: string) =>
        onSelect(CrmSyncOptionsEnum.LISTS, listId),
      );
    }

    if (type === CrmSyncOptionsEnum.ALL) {
      return (
        <Fragment key={type}>
          <RadioButton isDisabled={isDisabled} isChecked={isChecked} onChange={() => onSelect(type)}>
            {value.optionText}
            {allResourceCount !== undefined && (
              <StyledCountSpan>({allResourceCount.toLocaleString()})</StyledCountSpan>
            )}
          </RadioButton>
          {isChecked &&
            maxCountError &&
            maybeRenderMaxCountExceededError(resourceConfig.maxNumberToSync, maxCountError, allResourceCount)}
        </Fragment>
      );
    }

    if (type === CrmSyncOptionsEnum.ACCOUNTS) {
      return renderFromAccountsOption(isDisabled, isChecked, value.optionText, () => onSelect(type));
    }
    return null;
  });
}

function renderFromAccountsOption(
  isDisabled: boolean,
  isChecked: boolean,
  label: ReactNode,
  onChange: HandlerOf<boolean>,
) {
  return (
    <Fragment key={CrmSyncOptionsEnum.ACCOUNTS}>
      <RadioButton isDisabled={isDisabled} isChecked={!isDisabled && isChecked} onChange={onChange}>
        {label}
        {isDisabled && (
          <StyledTooltipCoordinator
            render={() => (
              <Trans id="crm-syncs-page-options-from-accounts-disabled-tooltip">
                Accounts sync must be enabled
              </Trans>
            )}
            placement="top"
            delay={0}
            persistent
            maxWidth={maxTooltipWidth}
          />
        )}
      </RadioButton>
    </Fragment>
  );
}

function renderLoadingState() {
  return <Skeleton width="600px" height="86px" borderRadius="8px" scheme={SkeletonColorSchemesEnum.GREY10} />;
}

function renderListsTooltip(tooltipText: ReactNode, learnMoreLink?: string) {
  return (
    <>
      {tooltipText}
      {learnMoreLink && (
        <StyledTooltipLearnMoreSpan onClick={() => openInBrowser(learnMoreLink)}>
          {' '}
          <Trans id="crm-syncs-page-options-lists-tooltip-learn-more">Learn more</Trans>
        </StyledTooltipLearnMoreSpan>
      )}
    </>
  );
}

function maybeRenderListsOption(
  props: CrmSyncsOptionsProps,
  syncOptions: CrmSyncsResourceOptions,
  maxCount: number,
  onSelectList: (listId?: string) => void,
) {
  const {isDisabled, isAdmin, selectedOption} = props;
  const listsOption = syncOptions[CrmSyncOptionsEnum.LISTS];
  if (!listsOption) {
    return null;
  }
  return (
    <Fragment key={CrmSyncOptionsEnum.LISTS}>
      <StyledRadioButton
        isDisabled={!isAdmin || isDisabled}
        isChecked={selectedOption?.type === CrmSyncOptionsEnum.LISTS}
        onChange={() => onSelectList()}
      >
        {listsOption.optionText}
        <StyledTooltipCoordinator
          render={() => renderListsTooltip(listsOption?.tooltip, listsOption?.learnMoreLink)}
          placement="top"
          delay={0}
          persistent
          maxWidth={maxTooltipWidth}
        />
      </StyledRadioButton>
      <StyledListsDropdownWrapper>
        {renderListsDropdown(props, listsOption.dropdownPlaceholder, maxCount, onSelectList)}
      </StyledListsDropdownWrapper>
    </Fragment>
  );
}

function renderListsDropdown(
  props: CrmSyncsOptionsProps,
  placeholder: ReactNode,
  maxCount: number,
  onSelectList: HandlerOf<string>,
) {
  return (
    <PopoverCoordinator
      isDisabled={false}
      renderButton={(hasHover: boolean) => renderListsDropdownButton(props, hasHover, placeholder, maxCount)}
      renderModal={(popoverProps: PopoverTransferProps) => (
        <CrmSyncsListsDropdown
          popoverProps={popoverProps}
          selectedListId={props.selectedOption?.listId}
          searchListsAsync={props.searchListsAsync}
          onSelect={(list) => onSelectList(list.id)}
          dropdownTitle={extractDropdownTitle(props.resourceConfig)}
        />
      )}
    />
  );
}

function renderListsDropdownButton(
  props: CrmSyncsOptionsProps,
  hasHover: boolean,
  placeholder: ReactNode,
  maxCount: number,
) {
  const {
    isAdmin,
    isDisabled,
    list,
    listError,
    isLoadingList,
    crmDisplayName,
    resourceConfig,
    selectedOption,
    disabledOptionType,
  } = props;
  if (isLoadingList) {
    return <StyledSkeleton />;
  }
  const maxCountError = extractMaxCountError(resourceConfig, CrmSyncOptionsEnum.LISTS);

  // if lists is selected but there's no list id or if the other selected option is disabled and they need to select a list
  const hasNoListId =
    (selectedOption?.type === CrmSyncOptionsEnum.LISTS && !selectedOption.listId) ||
    (selectedOption?.type !== CrmSyncOptionsEnum.LISTS && selectedOption?.type === disabledOptionType);
  return (
    <StyledDropdownBox
      hasHover={hasHover}
      isDisabled={!isAdmin || isDisabled}
      value={renderCustomListDropdownValueOrPlaceholder(
        list,
        props.selectedOption?.listId,
        placeholder,
        maxCount,
      )}
      boxStyles={{margin: 0, borderRadius: '6px', display: 'inline-flex'}}
      hasError={Boolean(listError) || hasNoListId}
      errorMessage={maybeRenderErrorMessage(crmDisplayName, maxCountError, hasNoListId, listError)}
    />
  );
}

function renderCustomListDropdownValueOrPlaceholder(
  list: CrmListModel | undefined,
  selectedListId: string | undefined,
  placeholder: ReactNode,
  maxCount: number,
) {
  if (!list || !selectedListId) {
    return placeholder;
  }

  if (isUndefined(list.numResources)) {
    return list?.name;
  }
  return (
    <>
      {list.name}
      <StyledCountSpan>{renderTotalCount(list.numResources, maxCount)}</StyledCountSpan>
    </>
  );
}

function renderTotalCount(totalCount: number, maxCount: number) {
  const count = totalCount <= maxCount ? totalCount : `${maxCount.toLocaleString()}+`;
  return <>({count.toLocaleString()})</>;
}

function maybeRenderMaxCountExceededError(maxCount: number, maxCountError: ReactNode, count?: number) {
  if (!count || count < maxCount) {
    return null;
  }
  return <StyledMaxCountErrorWrapperDiv>{maxCountError}</StyledMaxCountErrorWrapperDiv>;
}

function maybeRenderErrorMessage(
  crmDisplayName: string,
  maxCountError: ReactNode,
  hasNoListId: boolean,
  listError?: ListErrorsEnum,
) {
  if (hasNoListId) {
    return (
      <StyledListErrorWrapperDiv>
        <Trans id="crm-syncs-page-options-has-no-list-error">List view required.</Trans>
      </StyledListErrorWrapperDiv>
    );
  }

  if (!listError) {
    return undefined;
  }

  switch (listError) {
    case ListErrorsEnum.LIST_DELETED:
      return (
        <StyledListErrorWrapperDiv>
          <Trans id="crm-syncs-page-options-list-deleted-error">
            List has been deleted in {crmDisplayName}.
          </Trans>
        </StyledListErrorWrapperDiv>
      );
    case ListErrorsEnum.MAX_LIMIT_REACHED:
      return maxCountError && <StyledListErrorWrapperDiv>{maxCountError}</StyledListErrorWrapperDiv>;
    case ListErrorsEnum.UNKNOWN:
    default:
      return (
        <StyledListErrorWrapperDiv>
          <Trans id="crm-syncs-page-options-unknown-error">An error occurred loading the list.</Trans>
        </StyledListErrorWrapperDiv>
      );
  }
}

function extractMaxCountError(resourceConfig: CrmSyncsPageResourceConfig, type: CrmSyncOptionsEnum) {
  if (type === CrmSyncOptionsEnum.LISTS) {
    return resourceConfig?.syncOptions[CrmSyncOptionsEnum.LISTS]?.maxCountError;
  }
  if (type === CrmSyncOptionsEnum.ALL) {
    return resourceConfig?.syncOptions[CrmSyncOptionsEnum.ALL]?.maxCountError;
  }
  return undefined;
}

function extractDropdownTitle(resourceConfig: CrmSyncsPageResourceConfig) {
  return resourceConfig?.syncOptions[CrmSyncOptionsEnum.LISTS]?.dropdownTitle;
}
